import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type {
  FinanceSettings,
  FinanceSettingsInput,
  Contact,
  ContactCreateInput,
  ContactUpdateInput,
  ContactListParams,
  Unit,
  UnitCreateInput,
  UnitUpdateInput,
  UnitListParams,
  UnitContactInput,
  ParkingSpace,
  ParkingSpaceCreateInput,
  ParkingSpaceUpdateInput,
  FeeAdjustmentType,
  FeeAdjustmentTypeInput,
  UnitFeeAdjustment,
  UnitFeeAdjustmentInput,
  MonthlyBill,
  BillGenerateInput,
  BillUpdateInput,
  BillListParams,
  BillPreview,
  BankFileUpload,
  BankFileType,
  UnmatchedRecord,
  UnmatchedListParams,
  ResolveUnmatchedInput,
  FinanceDashboardSummary,
  FinancePagination,
} from '@/types/finance'
import * as financeApi from '../../services/api/finance'

// ========== 狀態介面定義 ==========

export interface FinanceState {
  // 專案 ID
  currentProjectId: string | null

  // 財務設定
  settings: FinanceSettings | null
  settingsLoading: boolean
  settingsError: string | null

  // 儀表板
  dashboard: FinanceDashboardSummary | null
  dashboardLoading: boolean
  dashboardError: string | null

  // 聯絡人
  contacts: Contact[]
  contactsPagination: FinancePagination | null
  contactsLoading: boolean
  contactsError: string | null
  activeContact: Contact | null

  // 房屋單位
  units: Unit[]
  unitsPagination: FinancePagination | null
  unitsLoading: boolean
  unitsError: string | null
  activeUnit: Unit | null

  // 停車位
  parkingSpaces: ParkingSpace[]
  parkingSpacesPagination: FinancePagination | null
  parkingSpacesTypeCounts: Record<string, number> | null
  parkingSpacesLoading: boolean
  parkingSpacesError: string | null

  // 費用調整類型
  feeAdjustmentTypes: FeeAdjustmentType[]
  feeAdjustmentTypesLoading: boolean
  feeAdjustmentTypesError: string | null

  // 單位費用調整
  unitFeeAdjustments: UnitFeeAdjustment[]
  unitFeeAdjustmentsLoading: boolean

  // 帳單
  bills: MonthlyBill[]
  billsPagination: FinancePagination | null
  billsSummary: {
    totalAmount: number
    paidAmount: number
    totalCount: number
    paidCount: number
    unpaidCount: number
  } | null
  billsLoading: boolean
  billsError: string | null
  activeBill: MonthlyBill | null
  billPreviews: BillPreview[]
  billPreviewsLoading: boolean

  // 銀行檔案上傳
  uploadHistory: BankFileUpload[]
  uploadHistoryPagination: FinancePagination | null
  uploadHistoryLoading: boolean
  uploadHistoryError: string | null
  uploading: boolean

  // 未匹配記錄
  unmatchedRecords: UnmatchedRecord[]
  unmatchedPagination: FinancePagination | null
  unmatchedLoading: boolean
  unmatchedError: string | null
}

const initialState: FinanceState = {
  currentProjectId: null,

  settings: null,
  settingsLoading: false,
  settingsError: null,

  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,

  contacts: [],
  contactsPagination: null,
  contactsLoading: false,
  contactsError: null,
  activeContact: null,

  units: [],
  unitsPagination: null,
  unitsLoading: false,
  unitsError: null,
  activeUnit: null,

  parkingSpaces: [],
  parkingSpacesPagination: null,
  parkingSpacesTypeCounts: null,
  parkingSpacesLoading: false,
  parkingSpacesError: null,

  feeAdjustmentTypes: [],
  feeAdjustmentTypesLoading: false,
  feeAdjustmentTypesError: null,

  unitFeeAdjustments: [],
  unitFeeAdjustmentsLoading: false,

  bills: [],
  billsPagination: null,
  billsSummary: null,
  billsLoading: false,
  billsError: null,
  activeBill: null,
  billPreviews: [],
  billPreviewsLoading: false,

  uploadHistory: [],
  uploadHistoryPagination: null,
  uploadHistoryLoading: false,
  uploadHistoryError: null,
  uploading: false,

  unmatchedRecords: [],
  unmatchedPagination: null,
  unmatchedLoading: false,
  unmatchedError: null,
}

// ========== 錯誤處理輔助函式 ==========

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  // axios error: error.response.data.error 是後端帶來的人話訊息
  if (typeof error === 'object' && error !== null) {
    const resp = (error as { response?: { data?: { error?: unknown; message?: unknown } } }).response
    const data = resp?.data
    if (data) {
      if (typeof data.error === 'string' && data.error.length > 0) return data.error
      if (typeof data.message === 'string' && data.message.length > 0) return data.message
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

// ========== Async Thunks ==========

// 財務設定
export const fetchFinanceSettings = createAsyncThunk(
  'finance/fetchSettings',
  async (projectId: string, thunkAPI) => {
    try {
      const response = await financeApi.getFinanceSettings(projectId)
      return response.data?.settings
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取財務設定失敗'))
    }
  }
)

export const updateFinanceSettings = createAsyncThunk(
  'finance/updateSettings',
  async (args: { projectId: string; data: FinanceSettingsInput }, thunkAPI) => {
    try {
      const response = await financeApi.updateFinanceSettings(args.projectId, args.data)
      return response.data?.settings
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新財務設定失敗'))
    }
  }
)

// 儀表板
export const fetchDashboardSummary = createAsyncThunk(
  'finance/fetchDashboard',
  async (args: { projectId: string; billingPeriod?: string }, thunkAPI) => {
    try {
      const response = await financeApi.getDashboardSummary(args.projectId, args.billingPeriod)
      const { summary, alerts } = response.data || {}
      return summary ? { ...summary, alerts } : null
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取儀表板失敗'))
    }
  }
)

// 聯絡人
export const fetchContacts = createAsyncThunk(
  'finance/fetchContacts',
  async (args: { projectId: string; params?: ContactListParams }, thunkAPI) => {
    try {
      const response = await financeApi.getContacts(args.projectId, args.params)
      return response.data
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取聯絡人失敗'))
    }
  }
)

export const fetchContact = createAsyncThunk(
  'finance/fetchContact',
  async (args: { projectId: string; contactId: string }, thunkAPI) => {
    try {
      const response = await financeApi.getContact(args.projectId, args.contactId)
      return response.data?.contact
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取聯絡人詳情失敗'))
    }
  }
)

export const createContact = createAsyncThunk(
  'finance/createContact',
  async (args: { projectId: string; data: ContactCreateInput }, thunkAPI) => {
    try {
      const response = await financeApi.createContact(args.projectId, args.data)
      return response.data?.contact
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立聯絡人失敗'))
    }
  }
)

export const updateContact = createAsyncThunk(
  'finance/updateContact',
  async (args: { projectId: string; contactId: string; data: ContactUpdateInput }, thunkAPI) => {
    try {
      const response = await financeApi.updateContact(args.projectId, args.contactId, args.data)
      return response.data?.contact
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新聯絡人失敗'))
    }
  }
)

export const deleteContact = createAsyncThunk(
  'finance/deleteContact',
  async (args: { projectId: string; contactId: string }, thunkAPI) => {
    try {
      await financeApi.deleteContact(args.projectId, args.contactId)
      return args.contactId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除聯絡人失敗'))
    }
  }
)

// 房屋單位
export const fetchUnits = createAsyncThunk(
  'finance/fetchUnits',
  async (args: { projectId: string; params?: UnitListParams }, thunkAPI) => {
    try {
      const response = await financeApi.getUnits(args.projectId, args.params)
      return response.data
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取房屋單位失敗'))
    }
  }
)

export const fetchUnit = createAsyncThunk(
  'finance/fetchUnit',
  async (args: { projectId: string; unitId: string }, thunkAPI) => {
    try {
      const response = await financeApi.getUnit(args.projectId, args.unitId)
      return response.data?.unit
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取單位詳情失敗'))
    }
  }
)

export const createUnit = createAsyncThunk(
  'finance/createUnit',
  async (args: { projectId: string; data: UnitCreateInput }, thunkAPI) => {
    try {
      const response = await financeApi.createUnit(args.projectId, args.data)
      return response.data?.unit
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立房屋單位失敗'))
    }
  }
)

export const updateUnit = createAsyncThunk(
  'finance/updateUnit',
  async (args: { projectId: string; unitId: string; data: UnitUpdateInput }, thunkAPI) => {
    try {
      const response = await financeApi.updateUnit(args.projectId, args.unitId, args.data)
      return response.data?.unit
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新房屋單位失敗'))
    }
  }
)

export const deleteUnit = createAsyncThunk(
  'finance/deleteUnit',
  async (args: { projectId: string; unitId: string }, thunkAPI) => {
    try {
      await financeApi.deleteUnit(args.projectId, args.unitId)
      return args.unitId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除房屋單位失敗'))
    }
  }
)

// 單位聯絡人
export const addUnitContact = createAsyncThunk(
  'finance/addUnitContact',
  async (args: { projectId: string; unitId: string; data: UnitContactInput }, thunkAPI) => {
    try {
      const response = await financeApi.addUnitContact(args.projectId, args.unitId, args.data)
      return { unitId: args.unitId, unitContactId: response.data?.unitContact?.id }
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '新增單位聯絡人失敗'))
    }
  }
)

export const removeUnitContact = createAsyncThunk(
  'finance/removeUnitContact',
  async (args: { projectId: string; unitId: string; unitContactId: string }, thunkAPI) => {
    try {
      await financeApi.removeUnitContact(args.projectId, args.unitId, args.unitContactId)
      return { unitId: args.unitId, unitContactId: args.unitContactId }
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '移除單位聯絡人失敗'))
    }
  }
)

export const setUnitPayer = createAsyncThunk(
  'finance/setUnitPayer',
  async (args: { projectId: string; unitId: string; payerId: string | null }, thunkAPI) => {
    try {
      const response = await financeApi.setUnitPayer(args.projectId, args.unitId, args.payerId)
      return response.data?.unit
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '設定繳費人失敗'))
    }
  }
)

// 停車位
export const fetchParkingSpaces = createAsyncThunk(
  'finance/fetchParkingSpaces',
  async (args: {
    projectId: string
    page?: number
    limit?: number
    unitId?: string
    ownerId?: string
    type?: string
    search?: string
  }, thunkAPI) => {
    try {
      const response = await financeApi.getParkingSpaces(args.projectId, {
        page: args.page || 1,
        limit: args.limit || 100, // 預設每頁 100 筆
        unitId: args.unitId,
        ownerId: args.ownerId,
        type: args.type,
        search: args.search,
      })
      return response.data
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取停車位失敗'))
    }
  }
)

export const createParkingSpace = createAsyncThunk(
  'finance/createParkingSpace',
  async (args: { projectId: string; data: ParkingSpaceCreateInput }, thunkAPI) => {
    try {
      const response = await financeApi.createParkingSpace(args.projectId, args.data)
      return response.data?.parkingSpace
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立停車位失敗'))
    }
  }
)

export const updateParkingSpace = createAsyncThunk(
  'finance/updateParkingSpace',
  async (args: { projectId: string; spaceId: string; data: ParkingSpaceUpdateInput }, thunkAPI) => {
    try {
      const response = await financeApi.updateParkingSpace(args.projectId, args.spaceId, args.data)
      return response.data?.parkingSpace
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新停車位失敗'))
    }
  }
)

export const deleteParkingSpace = createAsyncThunk(
  'finance/deleteParkingSpace',
  async (args: { projectId: string; spaceId: string }, thunkAPI) => {
    try {
      await financeApi.deleteParkingSpace(args.projectId, args.spaceId)
      return args.spaceId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除停車位失敗'))
    }
  }
)

// 費用調整類型
export const fetchFeeAdjustmentTypes = createAsyncThunk(
  'finance/fetchFeeAdjustmentTypes',
  async (projectId: string, thunkAPI) => {
    try {
      const response = await financeApi.getFeeAdjustmentTypes(projectId)
      return response.data?.adjustmentTypes
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取費用調整類型失敗'))
    }
  }
)

export const createFeeAdjustmentType = createAsyncThunk(
  'finance/createFeeAdjustmentType',
  async (args: { projectId: string; data: FeeAdjustmentTypeInput }, thunkAPI) => {
    try {
      const response = await financeApi.createFeeAdjustmentType(args.projectId, args.data)
      return response.data?.adjustmentType
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立費用調整類型失敗'))
    }
  }
)

export const updateFeeAdjustmentType = createAsyncThunk(
  'finance/updateFeeAdjustmentType',
  async (args: { projectId: string; typeId: string; data: Partial<FeeAdjustmentTypeInput> }, thunkAPI) => {
    try {
      const response = await financeApi.updateFeeAdjustmentType(args.projectId, args.typeId, args.data)
      return response.data?.adjustmentType
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新費用調整類型失敗'))
    }
  }
)

export const deleteFeeAdjustmentType = createAsyncThunk(
  'finance/deleteFeeAdjustmentType',
  async (args: { projectId: string; typeId: string }, thunkAPI) => {
    try {
      await financeApi.deleteFeeAdjustmentType(args.projectId, args.typeId)
      return args.typeId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除費用調整類型失敗'))
    }
  }
)

// 單位費用調整
export const fetchUnitFeeAdjustments = createAsyncThunk(
  'finance/fetchUnitFeeAdjustments',
  async (args: { projectId: string; unitId: string; billingPeriod?: string }, thunkAPI) => {
    try {
      const response = await financeApi.getUnitFeeAdjustments(
        args.projectId,
        args.unitId,
        args.billingPeriod
      )
      return response.data?.adjustments
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取單位費用調整失敗'))
    }
  }
)

export const createUnitFeeAdjustment = createAsyncThunk(
  'finance/createUnitFeeAdjustment',
  async (args: { projectId: string; unitId: string; data: UnitFeeAdjustmentInput }, thunkAPI) => {
    try {
      const response = await financeApi.createUnitFeeAdjustment(
        args.projectId,
        args.unitId,
        args.data
      )
      return response.data?.adjustment
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立單位費用調整失敗'))
    }
  }
)

export const deleteUnitFeeAdjustment = createAsyncThunk(
  'finance/deleteUnitFeeAdjustment',
  async (args: { projectId: string; unitId: string; adjustmentId: string }, thunkAPI) => {
    try {
      await financeApi.deleteUnitFeeAdjustment(args.projectId, args.unitId, args.adjustmentId)
      return args.adjustmentId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除單位費用調整失敗'))
    }
  }
)

// 帳單
export const fetchBills = createAsyncThunk(
  'finance/fetchBills',
  async (args: { projectId: string; params?: BillListParams }, thunkAPI) => {
    try {
      const response = await financeApi.getBills(args.projectId, args.params)
      return response.data
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取帳單失敗'))
    }
  }
)

export const fetchBill = createAsyncThunk(
  'finance/fetchBill',
  async (args: { projectId: string; billId: string }, thunkAPI) => {
    try {
      const response = await financeApi.getBill(args.projectId, args.billId)
      return response.data?.bill
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取帳單詳情失敗'))
    }
  }
)

export const previewBills = createAsyncThunk(
  'finance/previewBills',
  async (args: { projectId: string; data: BillGenerateInput }, thunkAPI) => {
    try {
      const response = await financeApi.previewBills(args.projectId, args.data)
      return response.data?.previews
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '預覽帳單失敗'))
    }
  }
)

export const generateBills = createAsyncThunk(
  'finance/generateBills',
  async (args: { projectId: string; data: BillGenerateInput }, thunkAPI) => {
    try {
      const response = await financeApi.generateBills(args.projectId, args.data)
      return response.data?.bills
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '產生帳單失敗'))
    }
  }
)

export const updateBill = createAsyncThunk(
  'finance/updateBill',
  async (args: { projectId: string; billId: string; data: BillUpdateInput }, thunkAPI) => {
    try {
      const response = await financeApi.updateBill(args.projectId, args.billId, args.data)
      return response.data?.bill
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新帳單失敗'))
    }
  }
)

export const deleteBill = createAsyncThunk(
  'finance/deleteBill',
  async (args: { projectId: string; billId: string }, thunkAPI) => {
    try {
      await financeApi.deleteBill(args.projectId, args.billId)
      return args.billId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除帳單失敗'))
    }
  }
)

export const batchUpdateBills = createAsyncThunk(
  'finance/batchUpdateBills',
  async (args: { projectId: string; billIds: string[]; data: BillUpdateInput }, thunkAPI) => {
    try {
      const response = await financeApi.batchUpdateBills(args.projectId, args.billIds, args.data)
      return { billIds: args.billIds, data: args.data, updatedCount: response.data?.updatedCount }
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '批次更新帳單失敗'))
    }
  }
)

// 銀行檔案上傳
export const uploadBankFile = createAsyncThunk(
  'finance/uploadBankFile',
  async (args: { projectId: string; fileType: BankFileType; file: File }, thunkAPI) => {
    try {
      const response = await financeApi.uploadBankFile(args.projectId, args.fileType, args.file)
      return response.data?.upload
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '上傳銀行檔案失敗'))
    }
  }
)

export const fetchUploadHistory = createAsyncThunk(
  'finance/fetchUploadHistory',
  async (
    args: { projectId: string; params?: { page?: number; limit?: number; fileType?: BankFileType } },
    thunkAPI
  ) => {
    try {
      const response = await financeApi.getUploadHistory(args.projectId, args.params)
      return response.data
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取上傳歷史失敗'))
    }
  }
)

export const deleteBankUpload = createAsyncThunk(
  'finance/deleteBankUpload',
  async (args: { projectId: string; uploadId: string }, thunkAPI) => {
    try {
      await financeApi.deleteBankUpload(args.projectId, args.uploadId)
      return args.uploadId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除上傳記錄失敗'))
    }
  }
)

// 未匹配記錄
export const fetchUnmatchedRecords = createAsyncThunk(
  'finance/fetchUnmatchedRecords',
  async (args: { projectId: string; params?: UnmatchedListParams }, thunkAPI) => {
    try {
      const response = await financeApi.getUnmatchedRecords(args.projectId, args.params)
      return response.data
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '讀取未匹配記錄失敗'))
    }
  }
)

export const resolveUnmatchedRecord = createAsyncThunk(
  'finance/resolveUnmatchedRecord',
  async (args: { projectId: string; recordId: string; data: ResolveUnmatchedInput }, thunkAPI) => {
    try {
      const response = await financeApi.resolveUnmatchedRecord(args.projectId, args.recordId, args.data)
      return response.data?.record
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '處理未匹配記錄失敗'))
    }
  }
)

export const batchResolveUnmatched = createAsyncThunk(
  'finance/batchResolveUnmatched',
  async (args: { projectId: string; recordIds: string[]; action: 'ignore' }, thunkAPI) => {
    try {
      const response = await financeApi.batchResolveUnmatched(
        args.projectId,
        args.recordIds,
        args.action
      )
      return { recordIds: args.recordIds, resolvedCount: response.data?.resolvedCount }
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '批次處理未匹配記錄失敗'))
    }
  }
)

export const deleteUnmatchedRecord = createAsyncThunk(
  'finance/deleteUnmatchedRecord',
  async (args: { projectId: string; recordId: string }, thunkAPI) => {
    try {
      await financeApi.deleteUnmatchedRecord(args.projectId, args.recordId)
      return args.recordId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除記錄失敗'))
    }
  }
)

export const batchDeleteUnmatched = createAsyncThunk(
  'finance/batchDeleteUnmatched',
  async (args: { projectId: string; recordIds: string[] }, thunkAPI) => {
    try {
      const response = await financeApi.batchDeleteUnmatched(args.projectId, args.recordIds)
      return { recordIds: args.recordIds, deletedCount: response.data?.deletedCount }
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '批次刪除記錄失敗'))
    }
  }
)

// ========== Slice 定義 ==========

const financeSlice = createSlice({
  name: 'finance',
  initialState,
  reducers: {
    setCurrentProjectId: (state, action: PayloadAction<string | null>) => {
      state.currentProjectId = action.payload
    },
    clearFinanceState: (state) => {
      return { ...initialState, currentProjectId: state.currentProjectId }
    },
    clearSettingsError: (state) => {
      state.settingsError = null
    },
    clearContactsError: (state) => {
      state.contactsError = null
    },
    clearUnitsError: (state) => {
      state.unitsError = null
    },
    clearBillsError: (state) => {
      state.billsError = null
    },
    clearUnmatchedError: (state) => {
      state.unmatchedError = null
    },
    setActiveContact: (state, action: PayloadAction<Contact | null>) => {
      state.activeContact = action.payload
    },
    setActiveUnit: (state, action: PayloadAction<Unit | null>) => {
      state.activeUnit = action.payload
    },
    setActiveBill: (state, action: PayloadAction<MonthlyBill | null>) => {
      state.activeBill = action.payload
    },
    clearBillPreviews: (state) => {
      state.billPreviews = []
    },
  },
  extraReducers: (builder) => {
    builder
      // 財務設定
      .addCase(fetchFinanceSettings.pending, (state) => {
        state.settingsLoading = true
        state.settingsError = null
      })
      .addCase(fetchFinanceSettings.fulfilled, (state, action) => {
        state.settingsLoading = false
        state.settings = action.payload || null
      })
      .addCase(fetchFinanceSettings.rejected, (state, action) => {
        state.settingsLoading = false
        state.settingsError = (action.payload as string) || action.error.message || '讀取財務設定失敗'
      })
      .addCase(updateFinanceSettings.fulfilled, (state, action) => {
        if (action.payload) {
          state.settings = action.payload
        }
      })

      // 儀表板
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.dashboardLoading = true
        state.dashboardError = null
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.dashboardLoading = false
        state.dashboard = action.payload || null
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.dashboardLoading = false
        state.dashboardError = (action.payload as string) || action.error.message || '讀取儀表板失敗'
      })

      // 聯絡人
      .addCase(fetchContacts.pending, (state) => {
        state.contactsLoading = true
        state.contactsError = null
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contactsLoading = false
        state.contacts = action.payload?.contacts || []
        state.contactsPagination = action.payload?.pagination || null
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.contactsLoading = false
        state.contactsError = (action.payload as string) || action.error.message || '讀取聯絡人失敗'
      })
      .addCase(fetchContact.fulfilled, (state, action) => {
        if (action.payload) {
          state.activeContact = action.payload
        }
      })
      .addCase(createContact.fulfilled, (state, action) => {
        if (action.payload) {
          state.contacts.push(action.payload)
        }
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.contacts.findIndex((c) => c.id === action.payload!.id)
          if (index !== -1) {
            state.contacts[index] = action.payload
          }
          if (state.activeContact?.id === action.payload.id) {
            state.activeContact = action.payload
          }
        }
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.contacts = state.contacts.filter((c) => c.id !== action.payload)
        if (state.activeContact?.id === action.payload) {
          state.activeContact = null
        }
      })

      // 房屋單位
      .addCase(fetchUnits.pending, (state) => {
        state.unitsLoading = true
        state.unitsError = null
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.unitsLoading = false
        state.units = action.payload?.units || []
        state.unitsPagination = action.payload?.pagination || null
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.unitsLoading = false
        state.unitsError = (action.payload as string) || action.error.message || '讀取房屋單位失敗'
      })
      .addCase(fetchUnit.fulfilled, (state, action) => {
        if (action.payload) {
          state.activeUnit = action.payload
        }
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        if (action.payload) {
          state.units.push(action.payload)
        }
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.units.findIndex((u) => u.id === action.payload!.id)
          if (index !== -1) {
            state.units[index] = action.payload
          }
          if (state.activeUnit?.id === action.payload.id) {
            state.activeUnit = action.payload
          }
        }
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.units = state.units.filter((u) => u.id !== action.payload)
        if (state.activeUnit?.id === action.payload) {
          state.activeUnit = null
        }
      })
      .addCase(setUnitPayer.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.units.findIndex((u) => u.id === action.payload!.id)
          if (index !== -1) {
            state.units[index] = action.payload
          }
          if (state.activeUnit?.id === action.payload.id) {
            state.activeUnit = action.payload
          }
        }
      })

      // 停車位
      .addCase(fetchParkingSpaces.pending, (state) => {
        state.parkingSpacesLoading = true
        state.parkingSpacesError = null
      })
      .addCase(fetchParkingSpaces.fulfilled, (state, action) => {
        state.parkingSpacesLoading = false
        state.parkingSpaces = action.payload?.parkingSpaces || []
        state.parkingSpacesPagination = action.payload?.pagination || null
        state.parkingSpacesTypeCounts = action.payload?.typeCounts || null
      })
      .addCase(fetchParkingSpaces.rejected, (state, action) => {
        state.parkingSpacesLoading = false
        state.parkingSpacesError = (action.payload as string) || action.error.message || '讀取停車位失敗'
      })
      .addCase(createParkingSpace.fulfilled, (state, action) => {
        if (action.payload) {
          state.parkingSpaces.push(action.payload)
        }
      })
      .addCase(updateParkingSpace.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.parkingSpaces.findIndex((p) => p.id === action.payload!.id)
          if (index !== -1) {
            state.parkingSpaces[index] = action.payload
          }
        }
      })
      .addCase(deleteParkingSpace.fulfilled, (state, action) => {
        state.parkingSpaces = state.parkingSpaces.filter((p) => p.id !== action.payload)
      })

      // 費用調整類型
      .addCase(fetchFeeAdjustmentTypes.pending, (state) => {
        state.feeAdjustmentTypesLoading = true
        state.feeAdjustmentTypesError = null
      })
      .addCase(fetchFeeAdjustmentTypes.fulfilled, (state, action) => {
        state.feeAdjustmentTypesLoading = false
        state.feeAdjustmentTypes = action.payload || []
      })
      .addCase(fetchFeeAdjustmentTypes.rejected, (state, action) => {
        state.feeAdjustmentTypesLoading = false
        state.feeAdjustmentTypesError =
          (action.payload as string) || action.error.message || '讀取費用調整類型失敗'
      })
      .addCase(createFeeAdjustmentType.fulfilled, (state, action) => {
        if (action.payload) {
          state.feeAdjustmentTypes.push(action.payload)
        }
      })
      .addCase(updateFeeAdjustmentType.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.feeAdjustmentTypes.findIndex((t) => t.id === action.payload!.id)
          if (index !== -1) {
            state.feeAdjustmentTypes[index] = action.payload
          }
        }
      })
      .addCase(deleteFeeAdjustmentType.fulfilled, (state, action) => {
        state.feeAdjustmentTypes = state.feeAdjustmentTypes.filter((t) => t.id !== action.payload)
      })

      // 單位費用調整
      .addCase(fetchUnitFeeAdjustments.pending, (state) => {
        state.unitFeeAdjustmentsLoading = true
      })
      .addCase(fetchUnitFeeAdjustments.fulfilled, (state, action) => {
        state.unitFeeAdjustmentsLoading = false
        state.unitFeeAdjustments = action.payload || []
      })
      .addCase(fetchUnitFeeAdjustments.rejected, (state) => {
        state.unitFeeAdjustmentsLoading = false
      })
      .addCase(createUnitFeeAdjustment.fulfilled, (state, action) => {
        if (action.payload) {
          state.unitFeeAdjustments.push(action.payload)
        }
      })
      .addCase(deleteUnitFeeAdjustment.fulfilled, (state, action) => {
        state.unitFeeAdjustments = state.unitFeeAdjustments.filter((a) => a.id !== action.payload)
      })

      // 帳單
      .addCase(fetchBills.pending, (state) => {
        state.billsLoading = true
        state.billsError = null
      })
      .addCase(fetchBills.fulfilled, (state, action) => {
        state.billsLoading = false
        state.bills = action.payload?.bills || []
        state.billsPagination = action.payload?.pagination || null
        // 儲存從 API 回傳的統計數據 (包含整個篩選條件下的所有帳單)
        if (action.payload?.summary) {
          state.billsSummary = {
            totalAmount: Number(action.payload.summary.totalAmount || 0),
            paidAmount: Number(action.payload.summary.paidAmount || 0),
            totalCount: action.payload.summary.totalCount || 0,
            paidCount: action.payload.summary.paidCount || 0,
            unpaidCount: action.payload.summary.unpaidCount || 0,
          }
        } else {
          state.billsSummary = null
        }
      })
      .addCase(fetchBills.rejected, (state, action) => {
        state.billsLoading = false
        state.billsError = (action.payload as string) || action.error.message || '讀取帳單失敗'
      })
      .addCase(fetchBill.fulfilled, (state, action) => {
        if (action.payload) {
          state.activeBill = action.payload
        }
      })
      .addCase(previewBills.pending, (state) => {
        state.billPreviewsLoading = true
      })
      .addCase(previewBills.fulfilled, (state, action) => {
        state.billPreviewsLoading = false
        state.billPreviews = action.payload || []
      })
      .addCase(previewBills.rejected, (state) => {
        state.billPreviewsLoading = false
      })
      .addCase(generateBills.fulfilled, (state, action) => {
        if (action.payload) {
          state.bills = [...action.payload, ...state.bills]
          state.billPreviews = []
        }
      })
      .addCase(updateBill.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.bills.findIndex((b) => b.id === action.payload!.id)
          if (index !== -1) {
            state.bills[index] = action.payload
          }
          if (state.activeBill?.id === action.payload.id) {
            state.activeBill = action.payload
          }
        }
      })
      .addCase(deleteBill.fulfilled, (state, action) => {
        state.bills = state.bills.filter((b) => b.id !== action.payload)
        if (state.activeBill?.id === action.payload) {
          state.activeBill = null
        }
      })
      .addCase(batchUpdateBills.fulfilled, (state, action) => {
        if (action.payload) {
          const { billIds, data } = action.payload
          state.bills = state.bills.map((bill) =>
            billIds.includes(bill.id) ? { ...bill, ...data } : bill
          )
        }
      })

      // 銀行檔案上傳
      .addCase(uploadBankFile.pending, (state) => {
        state.uploading = true
        state.uploadHistoryError = null
      })
      .addCase(uploadBankFile.fulfilled, (state, action) => {
        state.uploading = false
        if (action.payload) {
          state.uploadHistory = [action.payload, ...state.uploadHistory]
        }
      })
      .addCase(uploadBankFile.rejected, (state, action) => {
        state.uploading = false
        state.uploadHistoryError =
          (action.payload as string) || action.error.message || '上傳銀行檔案失敗'
      })
      .addCase(fetchUploadHistory.pending, (state) => {
        state.uploadHistoryLoading = true
        state.uploadHistoryError = null
      })
      .addCase(fetchUploadHistory.fulfilled, (state, action) => {
        state.uploadHistoryLoading = false
        state.uploadHistory = action.payload?.uploads || []
        state.uploadHistoryPagination = action.payload?.pagination || null
      })
      .addCase(fetchUploadHistory.rejected, (state, action) => {
        state.uploadHistoryLoading = false
        state.uploadHistoryError =
          (action.payload as string) || action.error.message || '讀取上傳歷史失敗'
      })
      .addCase(deleteBankUpload.fulfilled, (state, action) => {
        state.uploadHistory = state.uploadHistory.filter((u) => u.id !== action.payload)
      })

      // 未匹配記錄
      .addCase(fetchUnmatchedRecords.pending, (state) => {
        state.unmatchedLoading = true
        state.unmatchedError = null
      })
      .addCase(fetchUnmatchedRecords.fulfilled, (state, action) => {
        state.unmatchedLoading = false
        state.unmatchedRecords = action.payload?.records || []
        state.unmatchedPagination = action.payload?.pagination || null
      })
      .addCase(fetchUnmatchedRecords.rejected, (state, action) => {
        state.unmatchedLoading = false
        state.unmatchedError =
          (action.payload as string) || action.error.message || '讀取未匹配記錄失敗'
      })
      .addCase(resolveUnmatchedRecord.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.unmatchedRecords.findIndex((r) => r.id === action.payload!.id)
          if (index !== -1) {
            state.unmatchedRecords[index] = action.payload
          }
        }
      })
      .addCase(batchResolveUnmatched.fulfilled, (state, action) => {
        if (action.payload) {
          const { recordIds } = action.payload
          state.unmatchedRecords = state.unmatchedRecords.map((record) =>
            recordIds.includes(record.id) ? { ...record, status: 'IGNORED' as const } : record
          )
        }
      })
      .addCase(deleteUnmatchedRecord.fulfilled, (state, action) => {
        state.unmatchedRecords = state.unmatchedRecords.filter((r) => r.id !== action.payload)
      })
      .addCase(batchDeleteUnmatched.fulfilled, (state, action) => {
        if (action.payload) {
          const { recordIds } = action.payload
          state.unmatchedRecords = state.unmatchedRecords.filter(
            (record) => !recordIds.includes(record.id)
          )
        }
      })
  },
})

export const {
  setCurrentProjectId,
  clearFinanceState,
  clearSettingsError,
  clearContactsError,
  clearUnitsError,
  clearBillsError,
  clearUnmatchedError,
  setActiveContact,
  setActiveUnit,
  setActiveBill,
  clearBillPreviews,
} = financeSlice.actions

export default financeSlice.reducer
